import api from '@/lib/api';

/**
 * paymentDecline.js (Phase 178)
 * ------------------------------
 * Turn a Stripe.js error (from confirmPayment / confirmSetup /
 * confirmCardPayment) into something a parent can act on, and report it to
 * the backend so the coach can see WHY a payment didn't go through.
 *
 * Mirrors backend/services/payment_declines.py (source of truth).
 */

const REASONS = {
    insufficient_funds: {
        title: 'Not enough funds on this card',
        reason: "The bank reported there isn't enough available balance for this amount.",
        suggestion: 'Try a different card, or add funds and try again.',
        retrySame: true,
    },
    expired_card: {
        title: 'This card has expired',
        reason: 'The expiry date on the card has passed.',
        suggestion: 'Use a current card.',
        retrySame: false,
    },
    incorrect_cvc: {
        title: "Security code didn't match",
        reason: 'The 3-digit code (4 on Amex) on the back of the card was entered incorrectly.',
        suggestion: 'Re-enter the card and double-check the security code.',
        retrySame: true,
    },
    invalid_cvc: 'incorrect_cvc',
    incorrect_number: {
        title: "Card number doesn't look right",
        reason: 'The card number was mistyped or is not a valid card.',
        suggestion: 'Re-enter the card number carefully.',
        retrySame: true,
    },
    invalid_number: 'incorrect_number',
    invalid_expiry_month: 'incorrect_number',
    invalid_expiry_year: 'incorrect_number',
    incorrect_zip: {
        title: "Billing ZIP didn't match",
        reason: "The ZIP / postal code entered doesn't match the card's billing address.",
        suggestion: 'Re-enter the card with the ZIP your bank has on file.',
        retrySame: true,
    },
    card_declined: {
        title: 'Your bank declined this card',
        reason: 'The bank declined the charge without giving a specific reason.',
        suggestion: 'Try another card, or call the number on the back of the card — banks often block an unfamiliar merchant the first time.',
        retrySame: false,
    },
    generic_decline: 'card_declined',
    do_not_honor: {
        title: 'Your bank declined this card',
        reason: 'The bank returned “do not honor” — a general refusal, often a fraud filter on a new merchant.',
        suggestion: 'Call your bank to approve the charge, or use another card.',
        retrySame: false,
    },
    transaction_not_allowed: {
        title: "This card doesn't allow this type of charge",
        reason: "The card isn't enabled for online or recurring payments.",
        suggestion: 'Use a different card.',
        retrySame: false,
    },
    card_not_supported: 'transaction_not_allowed',
    card_velocity_exceeded: {
        title: 'Too many attempts on this card',
        reason: "The bank's limit on transactions or amount for this card was exceeded.",
        suggestion: 'Wait a little while or use another card.',
        retrySame: false,
    },
    lost_card: { title: 'Card reported lost', reason: 'The bank has this card flagged as lost.', suggestion: 'Use a different card.', retrySame: false },
    stolen_card: { title: 'Card reported stolen', reason: 'The bank has this card flagged as stolen.', suggestion: 'Use a different card.', retrySame: false },
    pickup_card: 'stolen_card',
    fraudulent: {
        title: 'Charge blocked as suspicious',
        reason: 'The bank or card network blocked this as potentially fraudulent.',
        suggestion: 'Call your bank to approve it, or use another card.',
        retrySame: false,
    },
    authentication_required: {
        title: "Your bank needs to verify it's you",
        reason: "The bank requires an extra verification step that wasn't completed.",
        suggestion: 'Try again and complete the verification prompt from your bank, or use another card.',
        retrySame: true,
    },
    payment_intent_authentication_failure: 'authentication_required',
    setup_intent_authentication_failure: 'authentication_required',
    processing_error: {
        title: 'Temporary processing error',
        reason: 'Something went wrong between us and the card network — nothing was charged.',
        suggestion: 'Try again in a moment. If it keeps happening, use another card.',
        retrySame: true,
    },
    try_again_later: 'processing_error',
    issuer_not_available: 'processing_error',
    currency_not_supported: {
        title: "Card doesn't support this currency",
        reason: "The card can't be charged in the coach's currency.",
        suggestion: 'Use a different card.',
        retrySame: false,
    },
};

const DEFAULT = {
    title: "This card couldn't be charged",
    reason: 'The bank declined the payment.',
    suggestion: 'Try another card. If the problem continues, call the number on the back of the card.',
    retrySame: false,
};

/**
 * @param {object} stripeError  result.error from Stripe.js
 * @returns {{title, reason, suggestion, retrySame, code, declineCode, stripeMessage, isValidationError}}
 */
export function explainStripeError(stripeError) {
    const code = stripeError?.code || '';
    const declineCode = stripeError?.decline_code || '';
    const key = (declineCode || code || '').toLowerCase();
    let entry = REASONS[key];
    let hops = 0;
    while (typeof entry === 'string' && hops < 3) { entry = REASONS[entry]; hops += 1; }
    const base = entry && typeof entry === 'object' ? entry : DEFAULT;
    // Stripe.js "validation_error" = form incomplete; not a bank decline.
    const isValidationError = stripeError?.type === 'validation_error';
    return {
        ...base,
        code,
        declineCode,
        stripeMessage: stripeError?.message || '',
        isValidationError,
        key: key || 'unknown',
    };
}

/**
 * Fire-and-forget report to the backend. Never throws.
 * ctx: { context, coachSlug, instructorId, applicationId, enrollmentId,
 *        invoiceId, paymentIntentId, amountCents, packageName,
 *        parentEmail, parentName }
 */
export async function reportPaymentDecline(stripeError, ctx = {}) {
    try {
        if (!stripeError || stripeError.type === 'validation_error') return null;
        const r = await api.post('/payments/decline-report', {
            context: ctx.context || 'checkout',
            code: stripeError.code || null,
            decline_code: stripeError.decline_code || null,
            message: (stripeError.message || '').slice(0, 300),
            coach_slug: ctx.coachSlug || null,
            instructor_id: ctx.instructorId || null,
            application_id: ctx.applicationId || null,
            enrollment_id: ctx.enrollmentId || null,
            invoice_id: ctx.invoiceId || null,
            payment_intent_id: ctx.paymentIntentId || stripeError?.payment_intent?.id || stripeError?.setup_intent?.id || null,
            amount_cents: Number.isFinite(ctx.amountCents) ? ctx.amountCents : null,
            package_name: ctx.packageName || null,
            parent_email: ctx.parentEmail || null,
            parent_name: ctx.parentName || null,
        });
        return r.data;
    } catch {
        return null;
    }
}

/**
 * One-liner for forms: explain + report. Returns the explanation object the
 * `PaymentDeclineNotice` component renders.
 */
export function handleStripeDecline(stripeError, ctx = {}) {
    const explained = explainStripeError(stripeError);
    if (!explained.isValidationError) reportPaymentDecline(stripeError, ctx);
    return explained;
}
